import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";
import { Roles } from "./active-user/model/Roles";
import { SysFeatures } from "./system-config/model/SysFeatures";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { CubiqBrokerClient, CUBIQ_BROKER } from "shared/broker-client-services";
import { BfxConfigIDRefHelper } from "../helper-services/bfx-config-id-ref-helper";

import {
  ClientMessageBrokerFactory,
  ClientMessageBroker,
  FnArg,
  UiArguments,
} from "shared/provider-webworker";
import { Inject, Injectable } from "@angular/core";
import { PromiseSet } from "./promise/PromiseSet";

import { NgModule } from "@angular/core";
import { TokenGuard } from "./token/token-guard";
import { ActiveUserAdapter } from "./active-user/adapter/ActiveUserAdapter";
import { LocalServiceMessageBrokerFactory } from "./provider-broker/LocalServiceMessageBroker";

import {
  ActiveUserProfilePrimaryService,
  ActiveUserProfilePromiseService,
} from "./active-user/ActiveUserProfileService";

export { TokenGuard } from "./token/token-guard";

export { ActiveUser } from "./active-user/model/ActiveUser";
export { ProfileRule } from "./active-user/model/ProfileRule";
export { Roles } from "./active-user/model/Roles";
export { ActiveUserAdapter } from "./active-user/adapter/ActiveUserAdapter";

export { PromiseSet } from "./promise/PromiseSet";
export { PrimaryPromiseSet } from "./promise/PrimaryPromiseSet";

export {
  VisibilityModel,
  INBOX_ORDER_STATES,
  ACTIVE_ORDER_STATES,
  BENCHMARK_ACTIVE_STATES,
  BENCHMARK_REJECT_STATES,
  HEDGE_ORDER_ACTIVE_STATES,
  HEDGE_ORDER_REJECT_STATES,
} from "./active-user/model/VisibilityModel";

export { ActiveUserProfilePromiseService } from "./active-user/ActiveUserProfileService";

export { BfxConfigService } from "./system-config/bfx-config.service";

export { SysFeatures } from "./system-config/model/SysFeatures";

export const SYSTEM_CONFIG: string = "SystemConfigChannel";

export interface ISystemConfigService {
  canEnterOrder(): Promise<boolean>;
  canEnterAlgoOrder(): Promise<boolean>;
  canEnterGammaOrder(): Promise<boolean>;
  canEnterRFQ(): Promise<boolean>;
  canEnterBmarkOrder(): Promise<boolean>;
  canViewOrderBlotters(): Promise<boolean>;
  canViewQuoteBlotters(): Promise<boolean>;
  canViewInboxBlotter(): Promise<boolean>;
  canViewTradesBlotters(): Promise<boolean>;
  canViewDealerInterventionBlotters(): Promise<boolean>;
  canViewRfqDealerInterventionBlotters(): Promise<boolean>;
  canViewFindOrders(): Promise<boolean>;
  canViewFindDeals(): Promise<boolean>;
  canViewOperationsInterventionBlotter(): Promise<boolean>;
  canViewRiskTradesBlotters(): Promise<boolean>;
  hasOrdersVisibilityUnlimited(): Promise<boolean>;
  canConfirmTrade(): Promise<boolean>;
  canCancelTrade(): Promise<boolean>;
  canAddFillCommentToTrade(): Promise<boolean>;
  getUserType(): Promise<string>;
  getSystemName(): Promise<string>;
  getAllTradePermissions(): Promise<Array<boolean>>;
  getAllRoles(): Promise<Map<string, string>>;
  getSystemConfig(config: string): Promise<string>;
}

export abstract class SystemConfigPromiseService implements ISystemConfigService {
  abstract canEnterOrder(): Promise<boolean>;
  abstract canEnterAlgoOrder(): Promise<boolean>;
  abstract canEnterGammaOrder(): Promise<boolean>;
  abstract canEnterRFQ(): Promise<boolean>;
  abstract canEnterNdfOrder(): Promise<boolean>;
  abstract canEnterNdfAlgoOrder(): Promise<boolean>;
  abstract canEnterBmarkOrder(): Promise<boolean>;
  abstract canViewOrderBlotters(): Promise<boolean>;
  abstract canViewQuoteBlotters(): Promise<boolean>;
  abstract canViewInboxBlotter(): Promise<boolean>;
  abstract canViewTradesBlotters(): Promise<boolean>;
  abstract canViewDealerInterventionBlotters(): Promise<boolean>;
  abstract canViewRfqDealerInterventionBlotters(): Promise<boolean>;
  abstract canViewFindOrders(): Promise<boolean>;
  abstract canViewFindDeals(): Promise<boolean>;
  abstract canViewScratchPad(): Promise<boolean>;
  abstract canViewOperationsInterventionBlotter(): Promise<boolean>;
  abstract canViewRiskTradesBlotters(): Promise<boolean>;
  abstract hasOrdersVisibilityUnlimited(): Promise<boolean>;
  abstract canConfirmTrade(): Promise<boolean>;
  abstract canCancelTrade(): Promise<boolean>;
  abstract canAddFillCommentToTrade(): Promise<boolean>;
  abstract getAllTradePermissions(): Promise<Array<boolean>>;
  abstract getAllRoles(): Promise<Map<string, string>>;
  abstract hasUnlock(): Promise<boolean>;
  abstract hasRole(role: string): Promise<boolean>;
  abstract getUserType(): Promise<string>;
  abstract getSystemConfig(config: string): Promise<string>;
  abstract getSystemName(): Promise<string>;
  abstract isPrimed(): Promise<boolean>;
  abstract hardHasRole(role: string): boolean;
  abstract hardGetSystemConfig(config: string): string;
  abstract areBenchmarksEnabled(): Promise<boolean>;
  abstract areAlgosEnabled(): Promise<boolean>;
  abstract canViewConnectionMonitor(): Promise<boolean>;
  abstract canViewAlgoMonitor(): Promise<boolean>;

  /**
   * All system configs are returned as string, so we check for standard boolean reps
   */
  checkBooleanValue(value: string): boolean {
    return value == "Y" || value == "true" || value == "TRUE" || value == "1";
  }

  async getSystemConfigBoolean(config: string) {
    return this.checkBooleanValue(await this.getSystemConfig(config));
  }

  protected static _benchmarkShortName: string = "BM";
  static getBenchmarkDisplayName(): string {
    return SystemConfigPromiseService._benchmarkShortName;
  }
}

@Injectable()
export class SystemConfigPrimaryService
  extends SystemConfigPromiseService
  implements ISystemConfigService
{
  protected primed: PromiseSet<void> = new PromiseSet<void>();
  private myLoggedInUser: CubiqTS.UserImpl;
  private configsPrimed: boolean = false;
  private rolesPrimed: boolean = false;
  protected broker: ServiceMessageBroker;
  private rolesMap: Map<string, string> = new Map<string, string>();
  private configsMap: Map<string, string> = new Map<string, string>();
  private systemName: string = null;

  constructor(
    @Inject(CUBIQ_BROKER) private cubiqBroker: CubiqBrokerClient,
    protected factory: ServiceMessageBrokerFactory,
  ) {
    super();
    this.broker = this.factory.createMessageBroker(SYSTEM_CONFIG, true);
    this.broker.registerMethod("canEnterOrder", [], this.canEnterOrder.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "canEnterAlgoOrder",
      [],
      this.canEnterAlgoOrder.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "canEnterGammaOrder",
      [],
      this.canEnterGammaOrder.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod("canEnterRFQ", [], this.canEnterRFQ.bind(this), PRIMITIVE);
    this.broker.registerMethod("canEnterNdfOrder", [], this.canEnterNdfOrder.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "canEnterBmarkOrder",
      [],
      this.canEnterBmarkOrder.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "canViewOrderBlotters",
      [],
      this.canViewOrderBlotters.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "canViewQuoteBlotters",
      [],
      this.canViewQuoteBlotters.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "canViewInboxBlotter",
      [],
      this.canViewInboxBlotter.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "canViewTradesBlotters",
      [],
      this.canViewTradesBlotters.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "canViewDealerInterventionBlotters",
      [],
      this.canViewDealerInterventionBlotters.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "canViewRfqDealerInterventionBlotters",
      [],
      this.canViewRfqDealerInterventionBlotters.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "canViewFindOrders",
      [],
      this.canViewFindOrders.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod("canViewFindDeals", [], this.canViewFindDeals.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "canViewScratchPad",
      [],
      this.canViewScratchPad.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "hasOrdersVisibilityUnlimited",
      [],
      this.hasOrdersVisibilityUnlimited.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod("canConfirmTrade", [], this.canConfirmTrade.bind(this), PRIMITIVE);
    this.broker.registerMethod("canCancelTrade", [], this.canCancelTrade.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "canAddFillCommentToTrade",
      [],
      this.canAddFillCommentToTrade.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod("hasUnlock", [], this.hasUnlock.bind(this), PRIMITIVE);
    this.broker.registerMethod("hasRole", [PRIMITIVE], this.hasRole.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "getUserType",
      [PRIMITIVE, PRIMITIVE],
      this.getUserType.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getUserType",
      [PRIMITIVE, PRIMITIVE],
      this.getUserType.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getSystemConfig",
      [PRIMITIVE],
      this.getSystemConfig.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod("getSystemName", [], this.getSystemName.bind(this), PRIMITIVE);
    this.broker.registerMethod("isPrimed", [], this.isPrimed.bind(this), PRIMITIVE);
    // Loads all trade permissions
    this.broker.registerMethod(
      "getAllTradePermissions",
      [],
      this.getAllTradePermissions.bind(this),
      PRIMITIVE,
    );
    // Load all permissions
    this.broker.registerMethod("getAllRoles", [], this.getAllRoles.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "areBenchmarksEnabled",
      [],
      this.areBenchmarksEnabled.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "canViewConnectionMonitor",
      [],
      this.canViewConnectionMonitor.bind(this),
      PRIMITIVE,
    );

    // FIXME: Auth Replacement
    // this.cubiqBroker.token$.subscribe((tokenState) => {
    //   if (TokenState.PRESENT === tokenState) {
    //     this.prime();
    //   }
    // });
    this.prime();
  }

  protected prime(): void {
    let sysConfigsObservable = this.cubiqBroker.primeStaticData<CubiqTS.SystemSettingImpl[]>(
      CubiqTS.SystemSettingImpl.name,
    );

    sysConfigsObservable.subscribe(
      (configs: CubiqTS.SystemSettingImpl[]) => {
        this.systemName = this.cubiqBroker.getSystemName();
        this.myLoggedInUser = this.cubiqBroker.getLoggedInUser();
        if (Array.isArray(configs)) {
          configs.map((config: CubiqTS.SystemSettingImpl) =>
            this.configsMap.set(config.settingId, config.settingValue),
          );
        }
        this.initHelpers();
        this.postProcessFlags();
        this.configsPrimed = true;
      },
      (error: Error) => {
        logger.out.error("SystemConfigPrimaryService: sys settings prime failed");

        this.rejectWorkerPrime(error);
      },
      () => {
        if (!this.configsPrimed) {
          logger.out.error(
            "SystemConfigPrimaryService: sys settings prime did not map any SystemSettingImpl",
          );
        }

        if (this.rolesPrimed) {
          this.primed.resolve();
        }
      },
    );

    let rolesObservable = this.cubiqBroker.primeStaticData<string[]>("userRoles");

    rolesObservable.subscribe(
      (roles: string[]) => {
        if (Array.isArray(roles)) {
          roles.map((role: string) => this.rolesMap.set(role, role));
        }

        this.rolesPrimed = true;
      },
      (error: Error) => {
        this.rejectWorkerPrime(error);
      },
      () => {
        if (!this.rolesPrimed) {
          logger.out.error("roles prime did not map any userRoles");
        }

        if (this.configsPrimed) {
          this.primed.resolve();
        }
      },
    );
  }

  private initHelpers() {
    if (this.configsMap.has(SysFeatures.idNumericLength)) {
      let idNumericLength = Number(this.configsMap.get(SysFeatures.idNumericLength));
      let orderIDPrefix = this.configsMap.get(SysFeatures.orderIDPrefix);
      let strategyIDPrefix = this.configsMap.get(SysFeatures.strategyIDPrefix);
      let tradeIDPrefix = this.configsMap.get(SysFeatures.tradeIDPrefix);
      let allocationIDPrefix = this.configsMap.get(SysFeatures.allocationIDPrefix);

      BfxConfigIDRefHelper.defaultOpts["config"] = {
        idNumericLength: idNumericLength > 0 ? idNumericLength : 1,
        orderIDPrefix: typeof orderIDPrefix === "string" ? orderIDPrefix : "",
        strategyIDPrefix: typeof strategyIDPrefix === "string" ? strategyIDPrefix : "",
        tradeIDPrefix: typeof tradeIDPrefix === "string" ? tradeIDPrefix : "",
        allocationIDPrefix: typeof allocationIDPrefix === "string" ? allocationIDPrefix : "",
      };

      console.log(`%O`, BfxConfigIDRefHelper.defaultOpts);
    }
  }

  private postProcessFlags(): void {
    try {
      let value: any = this.configsMap.get(SysFeatures.fixingOrderShortName);
      if (value != null && value.length > 0) {
        SystemConfigPrimaryService._benchmarkShortName = value;
      }
    } catch (e) {
      logger.out.warn(
        this.constructor.name + ": Failed to post process system configuration parameters " + e,
      );
    }
  }

  canEnterOrder(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        (_) => {
          resolve(this.rolesMap.has(Roles.VIEW_ORDER_ENTRY));
        },
        (reason) => {
          logger.out.info(`canEnterOrder promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canEnterNdfOrder(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        (value) => {
          if (this.rolesMap.has(Roles.VIEW_ORDER_ENTRY)) {
            if (this.configsMap.has(SysFeatures.ndfEnabled)) {
              let feature: string = this.configsMap.get(SysFeatures.ndfEnabled);
              let spotTriggerNdf: string = this.configsMap.get(SysFeatures.ndfSpotTriggerEnabled);
              let fwdTriggerNdf: string = this.configsMap.get(SysFeatures.ndfFwdTriggerEnabled);
              let featureBolean = this.checkBooleanValue(feature);
              let spotBoolean = this.checkBooleanValue(spotTriggerNdf);
              let fwdBoolean = this.checkBooleanValue(fwdTriggerNdf);
              let canEnter = featureBolean == true && (spotBoolean == true || fwdBoolean == true);
              resolve(canEnter);
            }
          }
          resolve(false);
        },
        (reason) => {
          logger.out.info(`canEnterOrder promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canEnterAlgoOrder(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        (value) => {
          if (this.rolesMap.has(Roles.VIEW_ORDER_ENTRY)) {
            if (this.configsMap.has(SysFeatures.algosEnabled)) {
              let feature: string = this.configsMap.get(SysFeatures.algosEnabled);
              resolve(this.checkBooleanValue(feature));
            }
          }

          resolve(false);
        },
        (reason) => {
          logger.out.info(`canEnterAlgoOrder promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canEnterNdfAlgoOrder(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        (_) => {
          if (this.rolesMap.has(Roles.VIEW_ORDER_ENTRY)) {
            if (
              this.configsMap.has(SysFeatures.algosEnabled) &&
              this.configsMap.has(SysFeatures.ndfEnabled)
            ) {
              let algos: string = this.configsMap.get(SysFeatures.algosEnabled);
              let ndf: string = this.configsMap.get(SysFeatures.ndfEnabled);
              resolve(this.checkBooleanValue(algos) && this.checkBooleanValue(ndf));
            }
          }

          resolve(false);
        },
        (reason) => {
          logger.out.info(`canEnterNdfAlgoOrder promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canEnterBmarkOrder(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        (value) => {
          if (this.rolesMap.has(Roles.VIEW_ORDER_ENTRY)) {
            if (this.configsMap.has(SysFeatures.benchmarksEnabled)) {
              let feature: string = this.configsMap.get(SysFeatures.benchmarksEnabled);
              resolve(this.checkBooleanValue(feature));
            }
          }

          resolve(false);
        },
        (reason) => {
          logger.out.info(`canEnterAlgoOrder promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canEnterGammaOrder(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        (value) => {
          if (this.rolesMap.has(Roles.VIEW_ORDER_ENTRY)) {
            if (this.configsMap.has(SysFeatures.gammaStrategiesEnabled)) {
              let feature: string = this.configsMap.get(SysFeatures.gammaStrategiesEnabled);
              resolve(this.checkBooleanValue(feature));
            }
          }

          resolve(false);
        },
        (reason) => {
          logger.out.info(`canEnterGammaOrder promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canEnterRFQ(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          if (this.rolesMap.has(Roles.VIEW_ORDER_ENTRY)) {
            if (this.configsMap.has(SysFeatures.rfqEnabled)) {
              let feature: string = this.configsMap.get(SysFeatures.rfqEnabled);
              resolve(this.checkBooleanValue(feature));
            }
          }

          resolve(false);
        },
        (reason) => {
          logger.out.info(`canEnterRFQ promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canViewOrderBlotters(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.VIEW_ORDER_BOOK));
        },
        (reason) => {
          logger.out.error("ERROR:rejection canViewOrderBlotters:", reason);
          reject(reason);
        },
      );
    });
  }

  canViewAlgoMonitor(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.VIEW_ALGO_MONITOR));
        },
        (reason) => {
          logger.out.error("ERROR:rejection canViewOrderBlotters:", reason);
          reject(reason);
        },
      );
    });
  }

  canViewQuoteBlotters(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          if (this.rolesMap.has(Roles.VIEW_QUOTE_BOOK)) {
            if (this.configsMap.has(SysFeatures.rfqEnabled)) {
              let feature: string = this.configsMap.get(SysFeatures.rfqEnabled);
              resolve(this.checkBooleanValue(feature));
            }
          }

          resolve(false);
        },
        (reason) => {
          logger.out.info(`canEnterRFQ promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canViewInboxBlotter(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.VIEW_INBOX));
        },
        (reason) => {
          logger.out.info(`canViewInboxBlotter promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canViewTradesBlotters(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.VIEW_FILLS_BLOTTER));
        },
        (reason) => {
          logger.out.info(`canViewTradesBlotters promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canViewDealerInterventionBlotters(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.VIEW_DEALER_INTERVENTION));
        },
        (reason) => {
          logger.out.info(`canViewDealerInterventionBlotters promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canViewRfqDealerInterventionBlotters(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          if (this.rolesMap.has(Roles.VIEW_DEALER_INTERVENTION)) {
            if (this.configsMap.has(SysFeatures.rfqEnabled)) {
              let feature: string = this.configsMap.get(SysFeatures.rfqEnabled);
              resolve(this.checkBooleanValue(feature));
            }
          }

          resolve(false);
        },
        (reason) => {
          logger.out.info(`canViewRfqDealerInterventionBlotters promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canViewFindOrders(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(
            this.rolesMap.has(Roles.VIEW_FIND_ORDERS) ||
              this.rolesMap.has(Roles.VIEW_FIND_LIVE_ORDERS),
          );
        },
        (reason) => {
          logger.out.info(`canViewFindOrders promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canViewFindDeals(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.VIEW_FIND_DEALS));
        },
        (reason) => {
          logger.out.info(`canViewFindDeals promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canViewScratchPad(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.VIEW_SCRATCHPAD));
        },
        (reason) => {
          logger.out.info(`canViewScratchPad promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canViewOperationsInterventionBlotter(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.VIEW_OPERATIONS_INTERVENTION));
        },
        (reason) => {
          logger.out.info(`canViewOperationsInterventionBlotter promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canViewRiskTradesBlotters(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.VIEW_RISK_TRADE_BLOTTER));
        },
        (reason) => {
          logger.out.info(`canViewRiskTradeBlotter promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  hasOrdersVisibilityUnlimited(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.VISIBILITY_UNLIMITED));
        },
        (reason) => {
          logger.out.info(`hasOrdersVisibilityUnlimited promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canConfirmTrade(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.DEAL_ACK));
        },
        (reason) => {
          logger.out.info(`canConfirmTrade promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canCancelTrade(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.DEAL_CANCEL));
        },
        (reason) => {
          logger.out.info(`canCancelTrade promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  canAddFillCommentToTrade(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.ADD_FILL_COMMENT));
        },
        (reason) => {
          logger.out.info(`canAddFillCommentToTrade promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  getAllTradePermissions(): Promise<Array<boolean>> {
    return new Promise<Array<boolean>>((resolve, reject) => {
      this.primed.get().then(
        () => {
          let tradePermissions = new Array<boolean>();
          tradePermissions.push(this.rolesMap.has(Roles.DEAL_ACK));
          tradePermissions.push(this.rolesMap.has(Roles.DEAL_CANCEL));
          tradePermissions.push(this.rolesMap.has(Roles.ADD_FILL_COMMENT));
          resolve(tradePermissions);
        },
        (reason) => {
          logger.out.info(`getAllTradePermissions promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  getAllRoles(): Promise<Map<string, string>> {
    return new Promise<Map<string, string>>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap);
        },
        (reason) => {
          logger.out.info(`getAllRoles promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  hasUnlock(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.ORDER_UNLOCK));
        },
        (reason) => {
          logger.out.info(`hasUnlock promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  areBenchmarksEnabled(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          let setting: string = this.configsMap.get(SysFeatures.benchmarksEnabled);
          resolve(setting === "Y" || setting === "y");
        },
        (reason) => {
          logger.out.info(`areBenchmarksEnabled promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  areAlgosEnabled(): Promise<boolean> {
    return this.primed.get().then(() => {
      let setting: string = this.configsMap.get(SysFeatures.algosEnabled);
      return setting === "Y" || setting === "y";
    });
  }

  hasRole(role: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(role));
        },
        (reason) => {
          logger.out.info(`hasRole promise rejection:` + role + " : " + reason);
          reject(reason);
        },
      );
    });
  }

  hardHasRole(role: string): boolean {
    if (this.rolesPrimed) {
      return this.rolesMap.has(role);
    }
    throw "Roles not primed!";
  }

  getSystemConfig(config: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.primed.get().then(
        () => {
          let setting: string = this.configsMap.get(config);
          resolve(setting);
        },
        (reason) => {
          logger.out.info(`getSystemConfig promise rejection:` + config + " : " + reason);
          reject(reason);
        },
      );
    });
  }

  hardGetSystemConfig(config: string): string {
    if (this.configsPrimed) {
      return this.configsMap.get(config);
    }
    throw "Configs not primed!";
  }

  getUserType(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.primed.get().then(
        () => {
          switch (this.myLoggedInUser.userType) {
            case CubiqTS.UserType.CLIENT:
              resolve("Client");
              break;
            case CubiqTS.UserType.SALES:
              resolve("Sales");
              break;
            case CubiqTS.UserType.TRADER:
              resolve("Trader");
              break;
            case CubiqTS.UserType.ADMIN:
              resolve("Admin");
              break;
            case CubiqTS.UserType.CLIENT_TRADER:
              resolve("Client Trader");
              break;
            default:
              resolve("Anonymous");
              break;
          }
        },
        (reason: string) => {
          logger.out.info("getUserType promise rejection: " + reason);
          reject(reason);
        },
      );
    });
  }

  public getSystemName(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.systemName);
        },
        (reason: string) => {
          logger.out.info("getSystemName promise rejection: " + reason);
          reject(reason);
        },
      );
    });
  }

  private rejectWorkerPrime(error: any): void {
    console.debug("SystemConfigPrimaryService: Failed to prime: " + error);

    if (this.primed.isPending()) {
      this.primed.reject(error);
    } else if (this.primed.isResolved) {
      logger.out.error("SystemConfigPrimaryService: error after already resolved");
    }
  }

  isPrimed(): Promise<boolean> {
    return this.primed.get();
  }

  canViewConnectionMonitor(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.rolesMap.has(Roles.ROLE_VIEW_CONNECTION_STATUS));
        },
        (reason) => {
          logger.out.info(`canViewConnectionMonitor promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }

  hasAnyRoleOf(these: string[]): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.primed.get().then(
        () => {
          these.forEach((role) => {
            if (this.rolesMap.has(role)) {
              resolve(true);
            }
          });
          resolve(false);
        },
        (reason) => {
          logger.out.info(`hasAnyRoleOf promise rejection:` + reason);
          reject(reason);
        },
      );
    });
  }
}

@Injectable()
export class BfxSysConfigUtilService {
  public isPrimed = false;
  public get manualExecutionCallNameText(): string {
    return this._manualExecutionCallName;
  }
  public set manualExecutionCallNameText(value: string) {
    this._manualExecutionCallName = value;
  }

  private _manualExecutionCallName: string = "CALL";

  constructor(private sysConfigService: SystemConfigPromiseService) {
    this.preInit();
  }

  private async preInit() {
    let isPrimed = await this.sysConfigService.isPrimed();
    this.init();
  }
  protected init() {
    this.setValues();
    setTimeout(() => {
      this.isPrimed = true;
    }, 200);
  }
  protected async setValues() {
    let manualExecutionCallName = await this.sysConfigService.getSystemConfig(
      SysFeatures.manualExecutionCallName,
    );

    if (
      manualExecutionCallName != null &&
      typeof manualExecutionCallName === "string" &&
      manualExecutionCallName.length > 0
    ) {
      this.manualExecutionCallNameText = manualExecutionCallName;
    }
  }
}

@NgModule({
  providers: [
    TokenGuard,
    ActiveUserAdapter,
    ActiveUserProfilePrimaryService,
    {
      provide: ActiveUserProfilePromiseService,
      useExisting: ActiveUserProfilePrimaryService,
    },
    SystemConfigPrimaryService,
    {
      provide: SystemConfigPromiseService,
      useExisting: SystemConfigPrimaryService,
    },
    LocalServiceMessageBrokerFactory,
    {
      provide: ServiceMessageBrokerFactory,
      useExisting: LocalServiceMessageBrokerFactory,
    },
    BfxSysConfigUtilService,
  ],
})
export class AuthServicesModule {}
